/* Norrviva — shared interactions
   Mega-menu trigger/panel toggle, mobile drawer + progressive
   disclosure, locale-chip popover. Vanilla JS, no framework, loaded
   with defer so it runs after the DOM is parsed.
*/
(function () {
  'use strict';

  function syncMegaOpenClass() {
    var anyOpen = !!document.querySelector('[data-mega-panel]:not([hidden])');
    document.body.classList.toggle('nvv-mega-open', anyOpen);
  }

  function openMega(panel, trigger) {
    closeAllMegas(panel);
    positionMega(panel);
    panel.removeAttribute('hidden');
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('data-active', 'true');
    syncMegaOpenClass();
  }

  function closeAllMegas(except) {
    // If focus is inside a panel about to close (Escape / outside click),
    // return it to that panel's trigger so keyboard users aren't dropped
    // onto <body>.
    var refocus = null;
    var active = document.activeElement;
    document.querySelectorAll('[data-mega-panel]').forEach(function (panel) {
      if (panel === except) return;
      if (!panel.hasAttribute('hidden') && active && panel.contains(active)) {
        var key = panel.getAttribute('data-mega-panel');
        refocus = document.querySelector('[data-mega-trigger="' + key + '"]');
      }
      panel.setAttribute('hidden', '');
    });
    document.querySelectorAll('[data-mega-trigger]').forEach(function (trigger) {
      var key = trigger.getAttribute('data-mega-trigger');
      var matchingPanel = document.querySelector('[data-mega-panel="' + key + '"]');
      if (matchingPanel !== except) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.removeAttribute('data-active');
      }
    });
    if (refocus) refocus.focus();
    syncMegaOpenClass();
  }

  // Position a mega panel under its trigger by setting --mega-left on
  // the panel to the trigger's left edge (in px), measured relative to
  // the .nvv-header. Uses .closest() rather than .offsetParent so we
  // get a valid anchor even while the panel is still hidden (offsetParent
  // is null for display:none elements). Re-run on resize while open.
  function positionMega(panel) {
    var key = panel.getAttribute('data-mega-panel');
    var trigger = document.querySelector('[data-mega-trigger="' + key + '"]');
    var anchor = panel.closest('.nvv-header');
    if (!trigger || !anchor) return;
    var triggerRect = trigger.getBoundingClientRect();
    var anchorRect = anchor.getBoundingClientRect();
    var left = Math.max(0, triggerRect.left - anchorRect.left);
    panel.style.setProperty('--mega-left', left + 'px');
  }

  function initMega() {
    var triggers = document.querySelectorAll('[data-mega-trigger]');
    if (!triggers.length) return;

    // Grace-period close: leaving the trigger schedules a close,
    // entering the panel cancels it. Lets the cursor traverse the gap
    // between trigger and panel without the menu collapsing.
    var closeTimer = null;
    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () { closeAllMegas(null); }, 150);
    }
    function cancelClose() { clearTimeout(closeTimer); }

    triggers.forEach(function (trigger) {
      var key = trigger.getAttribute('data-mega-trigger');
      var panel = document.querySelector('[data-mega-panel="' + key + '"]');
      if (!panel) return;

      // Hover-open (desktop pointer devices). Switching from one
      // trigger to another seamlessly swaps the open panel via
      // openMega's internal closeAllMegas call.
      trigger.addEventListener('mouseenter', function () {
        cancelClose();
        openMega(panel, trigger);
      });
      trigger.addEventListener('mouseleave', scheduleClose);
      panel.addEventListener('mouseenter', cancelClose);
      panel.addEventListener('mouseleave', scheduleClose);

      // Click toggle — keyboard / touch users + tap-to-close-when-open.
      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        cancelClose();
        var isOpen = !panel.hasAttribute('hidden');
        if (isOpen) {
          panel.setAttribute('hidden', '');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.removeAttribute('data-active');
          syncMegaOpenClass();
        } else {
          openMega(panel, trigger);
        }
      });
    });

    // Click outside any mega closes them all
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-mega-panel]') && !e.target.closest('[data-mega-trigger]')) {
        closeAllMegas(null);
      }
    });

    // Escape closes
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllMegas(null);
    });

    // Re-position any open mega when the viewport changes width
    window.addEventListener('resize', function () {
      document.querySelectorAll('[data-mega-panel]:not([hidden])').forEach(positionMega);
    });
  }

  function initDrawer() {
    var drawer = document.querySelector('[data-drawer]');
    if (!drawer) return;

    var triggers = document.querySelectorAll('[data-drawer-trigger]');
    var closeBtns = document.querySelectorAll('[data-drawer-close]');
    var lastTrigger = null;

    function showView(name) {
      drawer.querySelectorAll('[data-mobile-view]').forEach(function (v) {
        if (v.getAttribute('data-mobile-view') === name) {
          v.removeAttribute('hidden');
        } else {
          v.setAttribute('hidden', '');
        }
      });
      // Switching views hides the element that had focus (the Wallpapers /
      // back button) and focus would silently drop to <body>, escaping the
      // trap — move it to the first control of the view just shown.
      if (drawer.getAttribute('data-open') === 'true') {
        var shown = drawer.querySelector('[data-mobile-view="' + name + '"]');
        var target = shown && shown.querySelector('a[href], button:not([disabled])');
        if (target) target.focus();
      }
    }

    function setExpanded(on) {
      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
    }

    // Visible focusable elements inside the drawer (hidden views drop out
    // via offsetParent — they are display:none while inactive).
    function focusables() {
      var els = drawer.querySelectorAll(
        'a[href], button:not([disabled]), select, input, textarea, [tabindex]:not([tabindex="-1"])'
      );
      return Array.prototype.filter.call(els, function (el) {
        return el.offsetParent !== null;
      });
    }

    function open(e) {
      if (e && e.currentTarget) lastTrigger = e.currentTarget;
      drawer.removeAttribute('hidden');
      drawer.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
      setExpanded(true);
      var closeBtn = drawer.querySelector('[data-drawer-close]');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      drawer.setAttribute('hidden', '');
      drawer.removeAttribute('data-open');
      document.body.style.overflow = '';
      showView('root');
      setExpanded(false);
      if (lastTrigger) lastTrigger.focus();
    }

    // The drawer covers the page (position:fixed inset:0), so while it is
    // open, Tab must wrap inside it instead of reaching the page behind.
    // Listener on document (not the drawer): if focus ever lands on <body>
    // (e.g. the focused element was hidden), the trap still catches Tab and
    // pulls focus back inside.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || drawer.getAttribute('data-open') !== 'true') return;
      var els = focusables();
      if (!els.length) return;
      var first = els[0];
      var last = els[els.length - 1];
      var inside = drawer.contains(document.activeElement);
      if (!inside) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    triggers.forEach(function (t) { t.addEventListener('click', open); });
    closeBtns.forEach(function (b) { b.addEventListener('click', close); });

    drawer.querySelectorAll('[data-mobile-go]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        showView(btn.getAttribute('data-mobile-go'));
      });
    });

    drawer.querySelectorAll('[data-mobile-back]').forEach(function (btn) {
      btn.addEventListener('click', function () { showView('root'); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') close();
    });
  }

  function initLocale() {
    var chips = document.querySelectorAll('[data-locale-chip]');
    if (!chips.length) return;

    chips.forEach(function (chip) {
      var button = chip.querySelector('[data-locale-button]');
      var popover = chip.querySelector('[data-locale-popover]');
      if (!button || !popover) return;

      // Drawer-position chips render statically expanded (per brief —
      // locale switcher at the foot of the drawer). Skip toggle wiring.
      if (chip.getAttribute('data-position') === 'drawer') {
        popover.removeAttribute('hidden');
        button.setAttribute('aria-expanded', 'true');
        return;
      }

      button.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = !popover.hasAttribute('hidden');
        if (isOpen) {
          popover.setAttribute('hidden', '');
          button.setAttribute('aria-expanded', 'false');
        } else {
          popover.removeAttribute('hidden');
          button.setAttribute('aria-expanded', 'true');
        }
      });
    });

    // Click outside closes any header-position chip popover
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-locale-chip]')) {
        document.querySelectorAll('[data-locale-chip][data-position="header"] [data-locale-popover]').forEach(function (p) {
          p.setAttribute('hidden', '');
        });
        document.querySelectorAll('[data-locale-chip][data-position="header"] [data-locale-button]').forEach(function (b) {
          b.setAttribute('aria-expanded', 'false');
        });
      }
    });
  }

  // Header sticky + scroll direction. Drives Creative's existing CSS
  // in theme-critical.css that targets .shopify-section-header /
  // .header-up / .header-down / .header-stuck — see the rules around
  // line 1762 of theme-critical.css. Our section gets the
  // .shopify-section-header + .js-section__header classes via the
  // section schema so those rules apply to our wrapper.
  //
  //  - --header-height / --header-section-height / --window-height are
  //    CSS vars Creative's hide-on-scroll transforms read; we set them
  //    here so the transform actually moves the header by its own
  //    height. Re-measured on resize.
  //  - IntersectionObserver on the section wrapper toggles
  //    body.header-stuck when sticky kicks in (1px out of view).
  //  - Scroll listener toggles body.header-up only — absence of the
  //    class is equivalent to header-down (no transform). Simpler and
  //    keeps the reveal-on-up bulletproof: removing the class lets
  //    transform fall back to its default (none) and CSS transitions
  //    the header back into view.
  function initHeader() {
    var header = document.querySelector('.nvv-header[data-floating="true"], .nvv-header[data-floating-mobile="true"]');
    if (!header) return;
    var section = header.closest('.shopify-section') || header;

    // Transparent-over-hero: flag the body so CSS can pull .main up
    // under the (sticky) header bar by --header-height. The per-template
    // decision is already baked into data-transparent-header by Liquid.
    if (header.getAttribute('data-transparent-header') === 'true') {
      document.body.classList.add('nvv-header-transparent');
    }
    if (header.getAttribute('data-transparent-header-mobile') === 'true') {
      document.body.classList.add('nvv-header-transparent-mobile');
    }

    function setHeights() {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
      document.documentElement.style.setProperty('--header-section-height', section.offsetHeight + 'px');
      document.documentElement.style.setProperty('--window-height', window.innerHeight + 'px');
    }
    setHeights();
    window.addEventListener('resize', setHeights, { passive: true });

    if ('IntersectionObserver' in window) {
      var stickyObserver = new IntersectionObserver(function (entries) {
        document.body.classList.toggle('header-stuck', entries[0].intersectionRatio < 1);
      }, { threshold: [1] });
      stickyObserver.observe(section);
    }

    var lastScroll = window.pageYOffset || 0;
    var threshold = header.offsetHeight;
    function onScroll() {
      var st = window.pageYOffset;
      if (st <= 0) {
        document.body.classList.remove('header-up');
        lastScroll = 0;
        return;
      }
      var diff = st - lastScroll;
      if (diff > 5 && st > threshold) {
        document.body.classList.add('header-up');
      } else if (diff < -5) {
        document.body.classList.remove('header-up');
      }
      lastScroll = st;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // Pause/play toggles for autoplaying ambience videos (WCAG 2.2.2).
  // A [data-nvv-video-toggle] button controls the <video> that shares its
  // parent element. data-user-paused on the video tells section-level
  // IntersectionObservers (e.g. the home panel's offscreen pause) not to
  // restart a video the visitor stopped. Under prefers-reduced-motion the
  // video starts paused on its poster instead of autoplaying.
  function initVideoToggles() {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.querySelectorAll('[data-nvv-video-toggle]').forEach(function (btn) {
      var vid = btn.parentElement && btn.parentElement.querySelector('video');
      if (!vid) return;

      function setState(playing) {
        btn.setAttribute('data-state', playing ? 'playing' : 'paused');
        var label = playing ? btn.getAttribute('data-label-pause') : btn.getAttribute('data-label-play');
        if (label) btn.setAttribute('aria-label', label);
      }

      btn.addEventListener('click', function () {
        if (vid.paused) {
          vid.dataset.userPaused = 'false';
          vid.play().catch(function () {});
        } else {
          vid.dataset.userPaused = 'true';
          vid.pause();
        }
      });

      // Keep the button honest whatever pauses/plays the video (the
      // observer, the browser, or this button).
      vid.addEventListener('play', function () { setState(true); });
      vid.addEventListener('pause', function () { setState(false); });

      if (reduce) {
        vid.removeAttribute('autoplay');
        vid.dataset.userPaused = 'true';
        vid.pause();
      }
      setState(!vid.paused);
    });
  }

  // Cart drawer note (snippets/norrviva-cart-note.liquid). The drawer
  // re-renders over AJAX via window.theme.cart.store, so the Liquid
  // conditional in the snippet is only correct at first paint. Subscribe to
  // the same store the samples builder uses and toggle which note is visible:
  //   · only Sample-type items → the refund note (samples are not returnable)
  //   · any non-sample item     → the default note
  //   · empty cart              → both hidden
  // Notes live inside the (inert) <template> AND the live drawer; querying the
  // document only reaches the live copies — the template's fragment is inert.
  function initCartNote() {
    function update(state) {
      var items = (state && state.products && state.products.items) || [];
      var count = items.length;
      var onlySamples = count > 0 && items.every(function (item) {
        return item.product_type === 'Sample';
      });
      // Drive visibility from a BODY attribute + CSS, never from the note
      // nodes themselves: the drawer re-clones its whole contents from a
      // server-rendered <template> on every cart change, so any state we
      // write onto the notes is thrown away by the next re-render (a rAF
      // defer still lost that race). <body> survives every re-render.
      var mode = count === 0 ? 'none' : onlySamples ? 'samples' : 'default';
      document.body.setAttribute('data-nvv-cart-note', mode);
    }

    // cart-store.js is an async module (top-level await) — it can finish
    // AFTER DOMContentLoaded, so a single existence check here silently
    // misses it. Retry briefly until the store appears.
    var tries = 0;
    (function attach() {
      var store = window.theme && window.theme.cart && window.theme.cart.store;
      if (!store) {
        if (tries++ < 50) setTimeout(attach, 200);
        return;
      }
      update(store.getState());
      store.subscribe(update);
    })();
  }

  // Cart drawer/notification: strip the ' (Sample)' suffix from the DISPLAYED
  // product title only. The '(Sample)' variant line already marks samples, so
  // the suffix reads as a duplicate. The real product title is left intact for
  // fulfilment — only the on-screen text node is edited.
  //
  // Server first paint is already stripped in snippets/cart-item.liquid, but on
  // every AJAX cart change Creative's cart-components.js re-injects the RAW
  // `product_title` via innerHTML (its own JS — we don't touch it). The drawer
  // also re-clones its contents from a server <template>. So a one-shot pass
  // loses the race: a MutationObserver on each cart container re-strips whenever
  // titles are (re)written, and a store-subscribe + rAF pass re-scans for
  // containers (the notification cart mounts lazily on other pages).
  // '(Bobbin)' is deliberately NOT stripped (B2C never sees bobbins).
  function initCartSampleTitle() {
    var SUFFIX = ' (Sample)';

    // Localised type labels ride on the norrviva-cart-note wrapper
    // (data-nvv-roll-label / data-nvv-sample-label) — locale-file strings,
    // so they translate with the store.
    function typeLabels() {
      var el = document.querySelector('[data-nvv-roll-label]');
      if (!el) return null;
      return {
        Roll: el.getAttribute('data-nvv-roll-label'),
        Sample: el.getAttribute('data-nvv-sample-label'),
      };
    }
    function storeItems() {
      // Creative's cart store keeps the live line items at state.products.items.
      try {
        return window.theme.cart.store.getState().products.items || [];
      } catch (e) {
        return [];
      }
    }

    function strip(root) {
      (root || document).querySelectorAll('cart-item-title').forEach(function (el) {
        // Guarded write: once the suffix is gone the node no longer matches, so
        // the observer callback our own edit triggers finds nothing and stops —
        // no infinite loop.
        if (el.textContent.indexOf(SUFFIX) !== -1) {
          el.textContent = el.textContent.split(SUFFIX).join('');
        }
      });
      decorate(root);
    }

    // Re-stamp the "Wallpaper roll" / "Sample, A4" line and repoint sample
    // links at the parent roll PDP. The Liquid versions of both (cart-item.liquid)
    // only survive the server first paint — Creative's cart JS re-clones items
    // from a skeleton <template> on every cart change, so this runs from the
    // same MutationObserver that re-strips titles. All writes are guarded
    // (only when the value differs) so our own edits don't loop the observer.
    function decorate(root) {
      var items = storeItems();
      if (!items.length) return;
      var labels = typeLabels();
      (root || document).querySelectorAll('cart-item[key]').forEach(function (node) {
        var key = node.getAttribute('key');
        var item = null;
        for (var i = 0; i < items.length; i++) {
          if (items[i].key === key) { item = items[i]; break; }
        }
        if (!item) return;

        // Type line: skip when the variant line already carries text
        // (samples whose variant reads "Sample, A4" would double up).
        var micro = node.querySelector('cart-item-micro-copy');
        if (micro && labels && labels[item.product_type]) {
          var dp = micro.querySelector('cart-item-default-property');
          var dpText = dp ? dp.textContent.trim() : '';
          var span = micro.querySelector('.cart-item__typelabel');
          if (dpText) {
            if (span) span.remove();
          } else {
            if (!span) {
              span = document.createElement('span');
              span.className = 'cart-item__typelabel';
              micro.insertBefore(span, micro.firstChild);
            }
            if (span.textContent !== labels[item.product_type]) {
              span.textContent = labels[item.product_type];
            }
          }
        }

        // Sample rows link to the parent roll PDP (sample handles are
        // <roll-handle>-sample) — the sample PDP should never be reached;
        // its templateSuffix→redirect flips at launch as the backstop.
        if (item.product_type === 'Sample' && /-sample$/.test(item.handle || '')) {
          var parentUrl = '/products/' + item.handle.replace(/-sample$/, '');
          node.querySelectorAll('a[cart-item-url]').forEach(function (a) {
            var href = a.getAttribute('href') || '';
            // Guarded: parentUrl doesn't contain the -sample handle, so a
            // rewritten link no longer matches and the observer settles.
            if (href.indexOf(item.handle) !== -1) {
              a.setAttribute('href', parentUrl);
            }
          });
        }
      });
    }
    var observed = [];
    function observe(el) {
      if (!el || observed.indexOf(el) !== -1) return;
      observed.push(el);
      new MutationObserver(function () { strip(el); }).observe(el, {
        childList: true,
        subtree: true,
        characterData: true,
        // Creative fills item hrefs by attribute write only (no child churn) —
        // watch them so the sample→roll link rewrite never loses that race.
        // The rewrite is guarded (rewritten hrefs no longer match), so this
        // can't loop.
        attributes: true,
        attributeFilter: ['href'],
      });
      strip(el);
    }
    function observeAll() {
      document.querySelectorAll('.cart--draw, .cart--notification').forEach(observe);
    }
    observeAll();

    var tries = 0;
    (function attach() {
      var store = window.theme && window.theme.cart && window.theme.cart.store;
      if (!store) {
        if (tries++ < 50) setTimeout(attach, 200);
        return;
      }
      store.subscribe(function () {
        requestAnimationFrame(function () {
          observeAll();
          strip();
        });
      });
      // Immediate pass now that the store exists — the DOMContentLoaded run
      // decorated nothing (no store yet), and the server paint may never
      // mutate again to retrigger the observer.
      strip();
    })();
  }

  // Search drawer "You may also like" row (snippets/norrviva-search-suggest.liquid).
  // A quiet row of the five pattern collections, shown BELOW the live predictive
  // results. All tiles are server-rendered hidden; this reveals up to four of them
  // once the shopper has typed a query, dropping the tile whose pattern name the
  // query matches so we cross-sell the OTHER patterns.
  //
  // Visibility tracks the <predictive-search> element's [open] attribute (set by
  // predictive-search.js open()/close() when results are shown/hidden) AND a
  // non-empty query, so the row appears alongside results and clears with them.
  // If predictive search is disabled (no <predictive-search>), we fall back to
  // showing the row whenever the drawer input holds a query.
  function initSearchSuggest() {
    var row = document.querySelector('[data-search-suggest]');
    if (!row) return;

    var draw = row.closest('.search-draw') || document;
    var input = draw.querySelector('.js-search-input, input[type="search"]');
    if (!input) return;

    var ps = draw.querySelector('predictive-search');
    var tiles = Array.prototype.slice.call(row.querySelectorAll('[data-suggest-tile]'));
    var CAP = 4;

    function resultsOpen() {
      // No predictive-search element → results never render; drive by query alone.
      return ps ? ps.hasAttribute('open') : true;
    }

    function update() {
      var q = (input.value || '').trim().toLowerCase();

      if (!q || !resultsOpen()) {
        row.setAttribute('hidden', '');
        return;
      }

      var shown = 0;
      tiles.forEach(function (tile) {
        var name = (tile.getAttribute('data-name') || '').toLowerCase();
        var handle = (tile.getAttribute('data-handle') || '').toLowerCase();
        // Hide the pattern the shopper is already searching for (either direction:
        // query inside name for partial typing, name inside query for phrases).
        var isMatch =
          (name && (name.indexOf(q) !== -1 || q.indexOf(name) !== -1)) ||
          (handle && handle.indexOf(q) !== -1);

        if (isMatch || shown >= CAP) {
          tile.setAttribute('hidden', '');
        } else {
          tile.removeAttribute('hidden');
          shown++;
        }
      });

      if (shown === 0) row.setAttribute('hidden', '');
      else row.removeAttribute('hidden');
    }

    input.addEventListener('input', update);
    // The [open] attribute flips ~250ms after typing (debounced fetch), so mirror
    // the row to it: appears when results paint, clears when they close.
    if (ps) {
      new MutationObserver(update).observe(ps, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }
    update();
  }

  function init() {
    initMega();
    initDrawer();
    initLocale();
    initHeader();
    initVideoToggles();
    initCartNote();
    initCartSampleTitle();
    initSearchSuggest();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
